import { useContext, useEffect, useState, type ChangeEvent, type MouseEvent } from 'react';
import { SmartContext } from '../../../library/Core/SmartContext';
import { getDomainValueForCode, getFlexColDomainValueForCode, isEmpty, isSchoolAdmin } from '../../../library/Core/SmartFunctions';
import { DomainElement } from '../../../library/Core/SmartTypes';
import RecruiterDirectoryService from './RecruiterDirectoryService';
import SessionContext from '../../../library/Core/SessionContext';
import { Tooltip } from 'bootstrap';
import FactLoader from '../../../library/SimpleControls/FactLoader';
import DirectoryFilterDomainCheckboxes, { DirectoryShowProfileSection } from '../shared/DirectoryFilterDomainCheckboxes';
import { getActiveShowProfileFiltersFromConfig } from '../shared/directoryAppliedFilters';

export const handleClearAllFilterClick = (
    event: MouseEvent<HTMLElement>, // more generic
    state: any,
    dispatch: any
  ) => {
    event.preventDefault();
  
    if (state.data.searchBy.length > 0) {
      dispatch({
        type: 'CONTROL_VALUE_CHANGE',
        payload: { dataKey: 'filters.context.searchBy', value: '' },
      });
    }
  
    const defaultSearchCriteria = RecruiterDirectoryService.getDefaultSearchCriteria('');
    const criteriaForSelectedCard = RecruiterDirectoryService.getCriteriaForSelectedCard('totalJoinedAlumni', state);
    let criteria = { ...defaultSearchCriteria, ...criteriaForSelectedCard };
    criteria = { ...criteria,'context': { ...criteria.context, selectedCard : 'totalJoinedAlumni' } };
  
    dispatch({
      type: 'CONTROL_VALUE_CHANGE',
      payload: { dataKey: 'filters', value: criteria },
    });

    RecruiterDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch);

    // Note: After the initial load, Juniors, Seniors, Batch mates run based on the selected criteria. So removing the searchBy.
    if (state.data.searchBy.length > 0) {
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'filters.context.searchBy', value: '' } });
        dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchBy', value: '' } });
    }
};  

interface ExpandedControls {
    [key: string]: boolean;
}

const RecruiterSearchControl = () => {
    const uniqueCodes = new Set<string>();
    const { state, dispatch } = useContext(SmartContext);
    const { sessionState } = useContext(SessionContext);
    const userTypeCode = sessionState?.userTypeCode;
    const [performSearch, setPerformSearch] = useState(0);
    const [loading, setLoading] = useState(false);
    const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
    const [searchTerms, setSearchTerms] = useState({} as any);
    const [showFilters, setShowFilters] = useState(true);
    const [expandedControls, setExpandedControls] = useState<ExpandedControls>({});
    const [searchFilterDomains, setSearchFilterDomains] = useState(state?.data?.searchDomain);
    const [isOpen, setOpen] = useState(false);
    let displayClearAll = false;
    // Clone the filterConfig to modify it
    const modifiedFilterConfig = { ...RecruiterDirectoryService.filterConfig };
    let instituteTypeCode;
    console.log(sessionState);
    if(isSchoolAdmin()){
        const flexCol = getFlexColDomainValueForCode(sessionState?.primaryInstitute, 'INSTITUTE_DOMAIN', state);
        instituteTypeCode = isEmpty(flexCol) ? '' : JSON.parse(flexCol).institute_type;
    }else{
        instituteTypeCode=sessionState?.instituteTypeCode
    }
    
    const filters: any = Object.values(modifiedFilterConfig).filter(
        (filter: any) => filter.applicableFor === userTypeCode || filter.applicableFor === 'BOTH'
    );

    const [expandedDecades, setExpandedDecades] = useState<Record<string, boolean>>({});

    const toggleDecade = (label: string) => {
    setExpandedDecades(prev => ({ ...prev, [label]: !prev[label] }));
    };

    type YearDomain = { code: string | number; value: string };

    const makeDecadeBuckets = (domains: YearDomain[]) => {
    if (!domains?.length) return { beforeLabel: '', beforeYears: [] as YearDomain[], groups: [] as { from:number; to:number; label:string; years:YearDomain[] }[] };

    const nums = domains.map(d => parseInt(String(d.code), 10)).filter(Number.isFinite).sort((a,b)=>a-b);
    if (!nums.length) return { beforeLabel: '', beforeYears: [], groups: [] };

    const minD = Math.floor(nums[0]/10)*10;
    const maxD = Math.floor(nums[nums.length-1]/10)*10;

    const beforeYears = domains
        .filter(d => parseInt(String(d.code),10) < minD)
        .sort((a,b)=>parseInt(String(a.code),10)-parseInt(String(b.code),10));

    const groups: { from:number; to:number; label:string; years:YearDomain[] }[] = [];
    for (let d=minD; d<=maxD; d+=10) {
        const from=d, to=d+9;
        const years = domains
        .filter(x => {
            const y = parseInt(String(x.code),10);
            return y>=from && y<=to;
        })
        .sort((a,b)=>parseInt(String(a.code),10)-parseInt(String(b.code),10));
        if (years.length) groups.push({ from, to, label: `${from}-${to}`, years });
    }

    return { beforeLabel: beforeYears.length ? `Before ${minD}` : '', beforeYears, groups };
    };

    
    useEffect(() => {
        var tooltipTriggerList = [].slice.call(document.querySelectorAll('.defaultTooltip'));
        var tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new Tooltip(tooltipTriggerEl);
        });
    })

    useEffect(() => {
        let isActive = true;

        // pageLoader already fired the default search in parallel with other page-config
        // calls and stored results in state.data.students — skip the duplicate /search
        // call so the user sees results immediately without a second round-trip.
        if (performSearch === 0 && state.data.preloadedSearch) {
            setLoading(false);
            return () => { isActive = false; };
        }

        setLoading(true);

        const criteria =
            performSearch === 0
                ? { ...RecruiterDirectoryService.getCriteriaForSelectedCard('totalJoinedAlumni', state), ...state.data.filters }
                : state.data.filters;

        RecruiterDirectoryService.performSearchWithUserSelectedCriteria(criteria, state, dispatch)
            .finally(() => { if (isActive) setLoading(false); });

        // Note: After the initial load, Juniors, Seniors, Batch mates run based on the selected criteria. So removing the searchBy.
        if (state.data.searchBy.length > 0) {
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'filters.context.searchBy', value: '' } });
            dispatch({ type: 'CONTROL_VALUE_CHANGE', payload: { dataKey: 'searchBy', value: '' } });
        }

        return () => { isActive = false; };
    }, [performSearch]);

    const updateFilters = (type: string, dataKey: string, value: any) => {
        dispatch({ type, payload: { dataKey, value, errorMessages: [] } });
        setPerformSearch((prev) => prev + 1);
    };

    const handleSearchListItemSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value, checked } = event.target;
        const type = checked
            ? "CHECKBOX_ADD_NEW_RECORD_IN_ARRAY"
            : "DELETE_RECORD_IN_SIMPLE_ARRAY";

        if (id === "cityCode") {
            const cityDomainList = state?.data?.searchDomain?.cityCode || [];
            const clickedCity = cityDomainList.find(
            (c: any) => String(c.code) === String(value)
            );

            if (clickedCity && clickedCity.value?.trim()?.toLowerCase() === "others") {
            const mergedCodes =
                clickedCity.mergedCodes?.length > 0
                ? clickedCity.mergedCodes
                : [clickedCity.code];

            mergedCodes.forEach((code: string) => {
                updateFilters(type, `filters.${id}`, code);
            });

            return;
            }
        }
        updateFilters(type, `filters.${id}`, value);
    };

    const handleShowProfileFilterChange = (filterKey: string) => (event: ChangeEvent<HTMLInputElement>) => {
        const checkedValue = modifiedFilterConfig[filterKey]?.booleanCheckedValue ?? 1;
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${filterKey}`, event.target.checked ? checkedValue : null);
    };

    const handleSearchChange = (controlId: string) => (event: ChangeEvent<HTMLInputElement>) => {
        setSearchTerms((prevSearchTerms: any) => ({ ...prevSearchTerms, [controlId]: event.target.value }));
    };

    // const handleAppliedFilterClick = (event: any, key: string, value: string) => {
    //     event.preventDefault();
    //     // Remove the searchBy after the initial load. User can remove the years.
    //     if (state.data.searchBy.length > 0) updateFilters('CONTROL_VALUE_CHANGE', `filters.context.searchBy`, '');

    //     updateFilters('DELETE_RECORD_IN_SIMPLE_ARRAY', `filters.${key}`, value);
    // };

     const handleAppliedFilterClick = (event: any, key: string, value: string) => {
        event.preventDefault();
        if (state.data.searchBy.length > 0) {
            updateFilters("CONTROL_VALUE_CHANGE", `filters.context.searchBy`, "");
        }

        // Handle for multiple Time Others Comes in City filter 
        if (key === "cityCode") {
            const cityDomainList = state?.data?.searchDomain?.cityCode || [];
            const othersItem = cityDomainList.find(
            (d: any) =>
                d.value?.trim()?.toLowerCase() === "others" &&
                (String(d.code) === String(value) || d.mergedCodes?.includes(value))
            );

            if (othersItem) {
            const codesToRemove =
                othersItem.mergedCodes?.length > 0
                ? othersItem.mergedCodes
                : [othersItem.code];

            codesToRemove.forEach((code: string) => {
                updateFilters("DELETE_RECORD_IN_SIMPLE_ARRAY", `filters.${key}`, code);
            });

            return; 
            }
        }

        updateFilters("DELETE_RECORD_IN_SIMPLE_ARRAY", `filters.${key}`, value);
    };

    const handleBooleanFilterClear = (event: MouseEvent, key: string) => {
        event.preventDefault();
        if (state.data.searchBy.length > 0) {
            updateFilters('CONTROL_VALUE_CHANGE', `filters.context.searchBy`, '');
        }
        updateFilters('CONTROL_VALUE_CHANGE', `filters.${key}`, null);
    };

    const handleFilterToggle = () => {
        setOpen(!isOpen);
        setShowAdditionalFilters(!showAdditionalFilters);
    };

    // Function to toggle the expanded state of a control
    const toggleControl = (controlId: string) => {
        console.log('controlId', controlId);
        setExpandedControls((prevExpandedControls) => ({
            [controlId]: !prevExpandedControls[controlId],
        }));
    };

    console.log('state?.data?.filters', state?.data?.filters);

    return (
        <>
            <FactLoader show={loading} />
            <div>
            <section className="alumni_directory_filter container-fluid">
                <div className="max-1140 mx-auto">
                    <div className="JobInternshipListing">
                        <div className="ji_filtersec aos-init aos-animate">
                            <div className="mobileFilter d-flex justify-content-between align-items-center d-md-none mobileonly text-end">
                                <button
                                    className="btn-filter btn-unscroll font-18 font-500 w-100"
                                    type="button"
                                    onClick={handleFilterToggle}
                                    aria-expanded="true"
                                    aria-controls="filter_collapse">
                                    <i className="icon-filter"></i>
                                    <span>Filters</span>
                                    {/* <span> Selected Options (8)<i className="icon-right-arrow"></i></span> */}
                                </button>
                            </div>

                            <div className={`accordion collapse show directory_wrap ${isOpen ? 'open' : ''}`} id="filter_collapse">
                                <div className="filters-head">
                                    <h3
                                        className="font-18 font-500 mb-0"
                                        role="button"
                                        onClick={() => setShowFilters(!showFilters)}
                                        data-bs-toggle="collapse"
                                        data-bs-target="#accordionFilters"
                                        aria-controls="accordionFilters"
                                        aria-expanded={showFilters ? 'true' : 'false'}>
                                        <span className="icon-filter"></span>
                                        <span>Filters</span>
                                        <i className="btn-md-filter icon-right-arrow d-none d-md-block"></i>
                                    </h3>
                                    <a
                                        className="btn-clear btn-tab-clear d-block d-md-none"
                                        role="button"
                                        onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}>
                                        Clear
                                    </a>
                                   
                                </div>

                                {showFilters && (
                                    <div
                                        className="accordion-collapse collapse show"
                                        id="accordionFilters"
                                        data-bs-parent="filter_collapse">
                                        <div className="filterGroup mt-3">
                                            {Object.values(filters)
                                                .filter((control: any) => !control.isAdditional && control.isVisible && !control.isShowProfileFilter)
                                                .map((control: any, index: number) => (
                                                    <div key={control.id} className={control.class}>
                                                        <div className="filterHeader">
                                                            <div className="d-flex justify-content-between align-items-center position-relative " >
                                                                <button
                                                                    className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center "
                                                                    type="button"
                                                                    onClick={() => toggleControl(control.id)}
                                                                    data-bs-toggle="collapse"
                                                                    data-bs-target={`#collapse${control.id}`}
                                                                    aria-expanded={
                                                                        !isOpen
                                                                            ? 'true'
                                                                            : isOpen && (index === 0 || expandedControls[control.id])
                                                                            ? 'true'
                                                                            : 'false'
                                                                    }>
                                                                    <span className="font-18 font-600">{control.label}</span>
                                                                    <i className="bi bi-info-circle-fill defaultTooltip user-select-all" data-bs-toggle= 'tooltip' title={control.tooltip}></i>
                                                                </button>
                                                                
                                                            </div>
                                                        </div>
                                                        {/* <div className="collapse show" id={`collapse${control.id}`}> */}
                                                        <div
                                                            id={`collapse${control.id}`}
                                                            className={`accordion-collapse collapse ${
                                                                !isOpen
                                                                    ? 'show'
                                                                    : isOpen && (index === 0 || expandedControls[control.id])
                                                                    ? 'show'
                                                                    : ''
                                                            }`}
                                                            data-bs-parent="#accordionFilters">
                                                           
                                                            <div className="filterSearchblk">
                                                                <form className="d-flex align-items-center filterSearch position-relative">
                                                                    <input
                                                                        className="form-control flex-1 font-14 font-500 w-100"
                                                                        type="search"
                                                                        placeholder="Search"
                                                                        aria-label="Search"
                                                                        name={`${control.id}_Search`}
                                                                        id={`${control.id}_Search`}
                                                                        onChange={handleSearchChange(control.id)}
                                                                    />
                                                                    <button
                                                                        className="btn icon-search"
                                                                        type="button"
                                                                        id={`${control.id}_Btn`}></button>
                                                                </form>
                                                            </div>
                                                           <div className="card card-body">
                                                                    <div className="overflow-hidden">
                                                                        <div className="scrollMe" id="PassoutYear_Searchdiv">
                                                                    {control.id === 'passOutYear' ? (
                                                                    (() => {
                                                                        const src: YearDomain[] = searchFilterDomains['passOutYear'] ?? [];
                                                                        const q = (searchTerms['passOutYear'] || '').toLowerCase();
                                                                        const filtered = !q
                                                                        ? src
                                                                        : src.filter(d =>
                                                                            String(d.value).toLowerCase().includes(q) ||
                                                                            String(d.code).toLowerCase().includes(q)
                                                                            );

                                                                        const { beforeLabel, beforeYears, groups } = makeDecadeBuckets(filtered);
                                                                        const selected = (state?.data?.filters?.passOutYear || []).map(String);

                                                                        const yearCheckbox = (yd: YearDomain) => {
                                                                        const codeStr = String(yd.code);
                                                                        const checked = selected.includes(codeStr);
                                                                        return (
                                                                            <div key={codeStr} className="form-check d-flex flex-wrap ms-1">
                                                                            <input
                                                                                id="passOutYear"
                                                                                className="form-check-input me-2"
                                                                                type="checkbox"
                                                                                value={yd.code}
                                                                                name="Passout_Name"
                                                                                checked={checked}
                                                                                onChange={handleSearchListItemSelect}
                                                                                onClick={(e) => e.stopPropagation()} 
                                                                            />
                                                                            <label
                                                                                className="form-check-label font-16 font-400 flex-1 pt-1"
                                                                                onClick={(e) => e.stopPropagation()} 
                                                                            >
                                                                                {yd.value}
                                                                            </label>
                                                                            </div>
                                                                        );
                                                                        };

                                                                        return (
                                                                        <div className="card card-body">
                                                                            <div className="overflow-hidden">
                                                                            <div className="" id="PassoutYear_Searchdiv">
                                                                                {beforeLabel ? (
                                                                                <>
                                                                                    <div className="form-check d-flex flex-wrap parent" aria-expanded={false}>
                                                                                    <i className="plus-minus-toggle" />
                                                                                    <label className="form-check-label font-16 font-400 flex-1 pt-1">
                                                                                        {beforeLabel}
                                                                                    </label>
                                                                                    </div>
                                                                                    <div className="child ps-4">
                                                                                    {beforeYears.map(yearCheckbox)}
                                                                                    </div>
                                                                                </>
                                                                                ) : null}
                                                                                {groups.map((g) => {
                                                                                const label = g.label;
                                                                                const open = !!expandedDecades[label];

                                                                                return (
                                                                                    <div key={label} className="mb-2">
                                                                                    <div
                                                                                        className={`form-check d-flex flex-wrap parent ${open ? '' : 'collapsed'}`}
                                                                                        role="button"
                                                                                        aria-expanded={open}        
                                                                                        onClick={() => toggleDecade(label)}
                                                                                    >
                                                                                        <i className="plus-minus-toggle" />
                                                                                        <label className="form-check-label font-16 font-400 flex-1 pt-1">
                                                                                        {label}
                                                                                        </label>
                                                                                    </div>
                                                                                    {open && (
                                                                                        <div className="child ps-4 show">
                                                                                        {g.years.map(yearCheckbox)}
                                                                                        </div>
                                                                                    )}
                                                                                    </div>
                                                                                );
                                                                                })}
                                                                            </div>
                                                                            </div>
                                                                        </div>
                                                                        );
                                                                    })()
                                                                    ) : (
                                                                    <>
                                                                    <DirectoryFilterDomainCheckboxes
                                                                        controlId={control.id}
                                                                        domainCategory={control.domain}
                                                                        controlConfig={control}
                                                                        items={searchFilterDomains[control.id]}
                                                                        searchTerm={searchTerms[control.id] || ''}
                                                                        dedupeCodes={control.id === 'programId' ? uniqueCodes : undefined}
                                                                        isChecked={(code) =>
                                                                            state?.data?.filters[control.id]
                                                                                ?.map(String)
                                                                                .includes(String(code))
                                                                        }
                                                                        onChange={handleSearchListItemSelect}
                                                                    />
                                                                    </>
                                                                )}
                                                                </div>
                                                            </div>
                                                            </div>

                                                        </div>
                                                    </div>
                                                ))}

                                            <DirectoryShowProfileSection
                                                title="Show Recruiters"
                                                filterConfig={modifiedFilterConfig}
                                                filterValues={state?.data?.filters}
                                                onBooleanChange={handleShowProfileFilterChange}
                                                tooltip="Filter recruiters by organization type and hiring category."
                                                part="desktop"
                                            />

                                            <div className="additionalfilter_wrap last mb-20">
                                                <a
                                                    href="javascript:;"
                                                    className="btn-additionalfilter my-3 collapsed"
                                                    onClick={() => setShowAdditionalFilters(!showAdditionalFilters)}
                                                    data-bs-toggle="collapse"
                                                    data-bs-target="#additionalfilter"
                                                    aria-controls="additionalfilter"
                                                    aria-expanded={showAdditionalFilters ? 'true' : 'false'}>
                                                    <span>ADDITIONAL FILTER</span>
                                                    <i className="btn-md-filter icon-right-arrow d-none d-md-block"></i>
                                                </a>
                                                {showAdditionalFilters && (
                                                    <div className="collapse show" id="additionalfilter">
                                                        <div className="filterGroup">
                                                            {Object.values(filters)
                                                                .filter((control: any) => control.isAdditional)
                                                                .map((control: any, index: number) => (
                                                                    <div key={control.id} className={control.class}>
                                                                        <div className="filterHeader">
                                                                            <div className="d-flex justify-content-between align-items-center position-relative">
                                                                                <button
                                                                                    className="btn-nostyle text-start flex-1 d-flex justify-content-between align-items-center"
                                                                                    type="button"
                                                                                    onClick={() => toggleControl(control.id)}
                                                                                    data-bs-toggle="collapse"
                                                                                    data-bs-target={`#collapse${control.id}`}
                                                                                    aria-expanded={
                                                                                        !isOpen
                                                                                            ? 'true'
                                                                                            : isOpen && expandedControls[control.id]
                                                                                            ? 'true'
                                                                                            : 'false'
                                                                                    }
                                                                                    aria-controls={`collapse${control.id}`}>
                                                                                    <span className="font-18 font-600">
                                                                                        {control.label}
                                                                                    </span>
                                                                                    <i className="bi bi-info-circle-fill defaultTooltip user-select-all" data-bs-toggle= 'tooltip' title={control.tooltip}></i>
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                        {/* <div className="collapse show" id={`collapse${control.id}`}> */}
                                                                        <div
                                                                            id={`collapse${control.id}`}
                                                                            className={`accordion-collapse collapse ${
                                                                                !isOpen
                                                                                    ? 'show'
                                                                                    : expandedControls[control.id] && isOpen
                                                                                    ? 'show'
                                                                                    : ''
                                                                            }`}
                                                                            data-bs-parent="#accordionFilters">
                                                                            <div className="filterSearchblk">
                                                                                <form className="d-flex align-items-center filterSearch position-relative">
                                                                                    <input
                                                                                        className="form-control flex-1 font-14 font-500 w-100"
                                                                                        type="search"
                                                                                        placeholder="Search"
                                                                                        aria-label="Search"
                                                                                        name={`${control.id}_Search`}
                                                                                        id={`${control.id}_Search`}
                                                                                        onChange={handleSearchChange(control.id)}
                                                                                    />
                                                                                    <button
                                                                                        className="btn icon-search"
                                                                                        type="button"
                                                                                        id={`${control.id}_Btn`}></button>
                                                                                </form>
                                                                            </div>
                                                                            <div className="card card-body">
                                                                                <div className="overflow-hidden">
                                                                                    <div className="scrollMe" id="PassoutYear_Searchdiv">
                                                                                        <DirectoryFilterDomainCheckboxes
                                                                                            controlId={control.id}
                                                                                            domainCategory={control.domain}
                                                                                            controlConfig={control}
                                                                                            items={searchFilterDomains[control.id]}
                                                                                            searchTerm={searchTerms[control.id] || ''}
                                                                                            isChecked={(code) =>
                                                                                                state?.data?.filters[control.id]
                                                                                                    ?.map(String)
                                                                                                    .includes(String(code))
                                                                                            }
                                                                                            onChange={handleSearchListItemSelect}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="d-flex d-md-none filter_close_apply">
                                    <a
                                        className="btn-filter-close collapsed"
                                        onClick={handleFilterToggle}
                                        href="javascript:;"
                                        data-bs-toggle="collapse"
                                        data-bs-target="#filter_collapse"
                                        aria-expanded="false"
                                        aria-controls="filter_collapse">
                                        Close
                                    </a>
                                    {/* <a className="btn-filter-apply" href="javascript:;">Apply</a> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section>
                <div className="max-1140 mx-auto mb-3">
                    <div className="row">
                        <div className="filterresult border-0 mb-0">
                            <ul>
                                {Object.keys(state?.data?.filters).map((key) => {
                                    const filterValues = state?.data?.filters[key];
                                    if (!Array.isArray(filterValues)) return null;
                                    let seenOthers = false;
                                    return filterValues.map((item: any) => {
                                    const domainCategoryCode: string =
                                        RecruiterDirectoryService.getDomainCategoryCodeForKey(key) as string;
                                    if (!domainCategoryCode) return null;

                                    const displayValue = getDomainValueForCode(
                                        item,
                                        domainCategoryCode,
                                        state
                                    );
                                    if (!displayValue) return null;
                                    // Here We Handle this because Multiple COuntries are multiple Cities (Id's comes Different for Others)
                                    if (key === "cityCode" && displayValue.trim().toLowerCase() === "others") {
                                        if (seenOthers) return null;
                                        seenOthers = true; 
                                    }

                                    if (!displayClearAll) displayClearAll = true;

                                    return (
                                        <li key={`${key}-${item}`}>
                                        <a href="#">
                                            {displayValue}
                                            <i
                                            className="icon-cross"
                                            onClick={(event) =>
                                                handleAppliedFilterClick(event, key, item)
                                            }
                                            ></i>
                                        </a>
                                        </li>
                                    );
                                    });
                                })}

                                {getActiveShowProfileFiltersFromConfig(state?.data?.filters, modifiedFilterConfig).map(
                                    ({ key, label }) => {
                                        if (!displayClearAll) displayClearAll = true;
                                        return (
                                            <li key={`show-profile-${key}`}>
                                                <a href="#">
                                                    {label}
                                                    <i
                                                        className="icon-cross"
                                                        onClick={(event) => handleBooleanFilterClear(event, key)}
                                                    />
                                                </a>
                                            </li>
                                        );
                                    }
                                )}

                                {displayClearAll && (
                                    <li className="clear">
                                    <a
                                        className="btn-clear"
                                        role="button"
                                        onClick={(e) => handleClearAllFilterClick(e, state, dispatch)}
                                    >
                                        Clear All
                                    </a>
                                    </li>
                                )}
                                </ul>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        </>
    );
};

export default RecruiterSearchControl;
